import { useState, useEffect, useRef } from "react";
import { db } from "../firebase/config"
import { storage, buildKey } from "../utils/storage";

import { collection, query, onSnapshot, getDocs } from "firebase/firestore";

export const useGetDocuments = (docCollection, { ttlMs = 5 * 60 * 1000, persist = "session", realTime = false } = {}) => {
  const [documents, setDocuments] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (cancelledRef.current) return;

    setLoading(true);
    console.log(`🔍 [useGetDocuments] Iniciando busca: ${docCollection}`);

    const cacheKey = buildKey(["col", docCollection]);
    const cached = storage.get(cacheKey, { namespace: "firestore" });
    if (cached) {
      console.log(`💾 [useGetDocuments] Cache encontrado para ${docCollection}: ${cached.length} itens`);
      setDocuments(cached);
      setLoading(false);
    } else {
      console.log(`❌ [useGetDocuments] Cache não encontrado para ${docCollection}, buscando do Firestore...`);
    }

    const collectionRef = collection(db, docCollection);
    
    
    //const q = query(collectionRef, orderBy("createdAt", "desc"));
    const q = query(collectionRef);


    if (realTime) {
      console.log(`👂 [useGetDocuments] Configurando listener em tempo real para ${docCollection}`);
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          console.log(`📨 [useGetDocuments] Listener recebeu snapshot para ${docCollection}:`, {
            size: snapshot.size,
            empty: snapshot.empty,
            metadata: snapshot.metadata?.fromCache ? 'cache' : 'server'
          });
          
          if (!cancelledRef.current) {
            const fresh = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            console.log(`✅ [useGetDocuments] ${docCollection} atualizado via listener: ${fresh.length} documentos`);
            
            if (fresh.length > 0) {
              console.log(`📋 [useGetDocuments] Primeiros 3 documentos de ${docCollection}:`, fresh.slice(0, 3).map(d => ({ id: d.id, ...Object.keys(d).filter(k => k !== 'id').slice(0, 2).reduce((acc, k) => ({ ...acc, [k]: d[k] }), {}) })));
            } else {
              console.warn(`⚠️ [useGetDocuments] ${docCollection} está vazio! Verifique se há dados no Firestore.`);
            }
            
            setDocuments(fresh);
            storage.set(cacheKey, fresh, { ttlMs, persist, namespace: "firestore" });
            setLoading(false);
          } else {
            console.log(`🚫 [useGetDocuments] Listener cancelado para ${docCollection}, ignorando snapshot`);
          }
        },
        (error) => {
          console.error(`❌ [useGetDocuments] ERRO no listener de ${docCollection}:`, {
            code: error.code,
            message: error.message,
            stack: error.stack
          });
          
          if (!cancelledRef.current) {
            // Ignorar erros internos do Firestore durante desenvolvimento (hot-reload)
            if (error?.message?.includes('INTERNAL ASSERTION') || 
                error?.message?.includes('Unexpected state') ||
                error?.code === 'ca9' || 
                error?.code === 'b815') {
              console.warn('⚠️ [useGetDocuments] Erro interno do Firestore ignorado:', docCollection);
              return;
            }
            
            // Erro de permissão
            if (error.code === 'permission-denied') {
              console.error(`🔒 [useGetDocuments] PERMISSÃO NEGADA para ${docCollection}! Verifique as regras do Firestore.`);
            }
            
            setError(error.message);
            setLoading(false);
          }
        }
      );
      
      console.log(`✅ [useGetDocuments] Listener configurado para ${docCollection}, aguardando dados...`);

      return () => {
        cancelledRef.current = true;
        try {
          unsubscribe();
        } catch (err) {
          console.warn('⚠️ [useGetDocuments] Erro ao remover listener (ignorado):', err);
        }
      };
    } else {
      (async () => {
        try {
          console.log(`🔍 [useGetDocuments] Buscando ${docCollection} do Firestore (sem realTime)...`);
          const snap = await getDocs(q);
          if (!cancelledRef.current) {
            const fresh = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            console.log(`✅ [useGetDocuments] ${docCollection} encontrados: ${fresh.length} documentos`);
            setDocuments(fresh);
            storage.set(cacheKey, fresh, { ttlMs, persist, namespace: "firestore" });
          }
        } catch (err) {
          if (!cancelledRef.current) {
            console.error(`❌ [useGetDocuments] Erro ao buscar ${docCollection}:`, err.code, err.message);
            setError(err.message);
          }
        } finally {
          if (!cancelledRef.current) {
            setLoading(false);
            console.log(`🏁 [useGetDocuments] Busca de ${docCollection} finalizada`);
          }
        }
      })();

      return () => {
        cancelledRef.current = true;
      };
    }
  }, [docCollection, ttlMs, persist, realTime]);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  return { documents, loading, error };
};